// One view per StageItem kind (spec-v4 section 8.4). Each is a big picture or panel plus one line of context and at
// most one button; choices and auditions have their own files.
import { useState, type ReactNode } from "react";
import { approve } from "../actions";
import { api } from "../api";
import { Icon } from "../components/bits";
import { Notice } from "../components/Notice";
import { SunoPackBody } from "../components/SunoPack";
import { writeFold } from "../fold";
import { budgetLine, warningNotices } from "../references";
import { navigate } from "../router";
import { playNow } from "../player";
import { useStore } from "../store";
import type { Project, SheetGroup, StageItem } from "../types";
import { minimiseStage } from "./actions";
import { ShowcaseCompare } from "../components/ShowcaseCompare";
import { compareClips, loadModelShowcase } from "../showcase";
import { StillCompare } from "../components/ImageShowcase";
import { compareStills, loadImageShowcase } from "../showcaseImages";
import { useStageLoad, type StageCharacter, type StageSheetVersion } from "./data";
import { FullFrame, FullFrameButton } from "../components/FullFrame";

type Of<K extends StageItem["kind"]> = Extract<StageItem, { kind: K }>;

/** The sheet viewer's tabs (core sheets.ts PANEL_GROUPS; web doesn't import core). */
export const SHEET_GROUPS: { id: SheetGroup; label: string; panels: { id: string; label: string }[] }[] = [
  { id: "angles", label: "Angles", panels: [{ id: "front", label: "Front" }, { id: "three_quarter", label: "3/4" }, { id: "side", label: "Side" }, { id: "back", label: "Back" }] },
  {
    id: "faces",
    label: "Faces",
    panels: [{ id: "neutral", label: "Neutral" }, { id: "happy", label: "Happy" }, { id: "sad", label: "Sad" }, { id: "angry", label: "Angry" }, { id: "surprised", label: "Surprised" }, { id: "thinking", label: "Thinking" }],
  },
  { id: "poses", label: "Poses", panels: [{ id: "standing", label: "Standing" }, { id: "walking", label: "Walking" }, { id: "sitting", label: "Sitting" }, { id: "pointing", label: "Pointing" }] },
];

const aspectOf = (p: Project | null | undefined) => (p?.brief.aspect ?? "16:9").replace(":", "x");

/** Leave the stage for a screen (the call stays on; the banner brings it back). */
export function openScreen(path: string) {
  minimiseStage();
  navigate(path);
}

/** A picture in a rounded frame, at the video's aspect. */
export function Shot({ src, aspect = "16x9", alt = "", children, empty }: { src?: string | null; aspect?: string; alt?: string; children?: ReactNode; empty?: string }) {
  return (
    <div className={`stg-shot frame-${aspect}`}>
      {src ? <img src={src} alt={alt} /> : <span className="stg-shot-empty">{empty ?? "Not made yet"}</span>}
      {children}
    </div>
  );
}

function Loading({ what }: { what: string }) {
  return <p className="stg-wait">Loading {what}…</p>;
}
function Failed({ what }: { what: string }) {
  return <p className="stg-wait">Couldn't load {what}.</p>;
}

function useProject(id: string) {
  return useStageLoad(`project:${id}`, (src) => src.project(id));
}

const sceneNo = (p: Project | null, sceneId: string) => (p?.script?.scenes.findIndex((s) => s.id === sceneId) ?? -1) + 1;

// ------------------------------------------------------------------ project / video things

function ProjectView({ it }: { it: Of<"project"> }) {
  const { data: p, error } = useProject(it.projectId);
  const here = useStore((s) => s.suiteProject === it.projectId);
  if (error) return <Failed what="the video" />;
  if (!p) return <Loading what="the video" />;
  const first = p.script?.scenes.find((s) => p.shots[s.id]?.frame?.url);
  const poster = p.thumbUrl ?? p.latestRender?.posterUrl ?? (first ? p.shots[first.id]!.frame!.url : null);
  const video = p.latestRender?.status === "done" ? p.latestRender.videoUrl : null;
  const scenes = p.script?.scenes.length ?? 0;
  return (
    <div className="stg-item">
      <Shot src={poster} aspect={aspectOf(p)} empty={p.script ? "No pictures yet" : "No script yet"}>
        {video && (
          <button data-action="stage.play" type="button" className="stg-play" onClick={() => playNow({ url: video, title: p.title, what: "render", projectId: p.id })} aria-label={`Play ${p.title}`}>
            <Icon name="play" size={26} />
          </button>
        )}
      </Shot>
      <p className="stg-meta">
        {scenes ? `${scenes} scenes` : "No script yet"} · {p.status === "done" ? "Rendered" : p.status === "rendering" ? "Rendering" : "In progress"}
      </p>
      {!here && (
        <button data-action="stage.open" type="button" className="btn btn-quiet btn-small" onClick={() => openScreen(`/p/${p.id}`)}>
          Open the video
        </button>
      )}
    </div>
  );
}

function FrameView({ it }: { it: Of<"frame"> }) {
  const { data: p } = useProject(it.projectId);
  const shot = p?.shots[it.sceneId]?.frame;
  const n = sceneNo(p, it.sceneId);
  const scene = p?.script?.scenes.find((s) => s.id === it.sceneId);
  const [busy, setBusy] = useState(false);
  // 2026-10-31: show_on_stage large=true opens the picture full screen; a tap on it does too
  const [full, setFull] = useState(!!it.large);
  const src = it.url ?? shot?.url;
  return (
    <div className="stg-item">
      {full && src && <FullFrame src={src} alt={scene?.imagePrompt ?? ""} title={n ? `Scene ${n}` : "Frame"} onClose={() => setFull(false)} />}
      <Shot src={src} aspect={aspectOf(p)} alt={scene?.imagePrompt ?? ""}>
        {src && <FullFrameButton label="Full screen" className="icon-btn preview-full" onOpen={() => setFull(true)} />}
      </Shot>
      <p className="stg-meta">
        {n ? `Scene ${n}` : "Frame"}
        {shot ? ` · ${shot.status === "approved" ? "approved" : "needs your OK"}` : ""}
      </p>
      {scene?.voiceover && <p className="stg-quote">“{scene.voiceover}”</p>}
      {shot?.status === "draft" && (
        <button
          data-action="stage.approve"
          type="button"
          className="btn btn-primary btn-small"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await approve(it.projectId, it.sceneId, "frame").finally(() => setBusy(false));
          }}
        >
          <Icon name="check" size={16} /> Approve
        </button>
      )}
    </div>
  );
}

/** 2026-11-03: any library picture the Director shows (a place, a prop, an upload); large opens it full screen. */
function PictureView({ it }: { it: Of<"picture"> }) {
  const [full, setFull] = useState(!!it.large);
  return (
    <div className="stg-item">
      {full && <FullFrame src={it.url} alt="" title={it.title ?? "Picture"} onClose={() => setFull(false)} />}
      <Shot src={it.url} alt={it.title ?? ""}>
        <FullFrameButton label="Full screen" className="icon-btn preview-full" onOpen={() => setFull(true)} />
      </Shot>
      {it.title && <p className="stg-meta">{it.title}</p>}
    </div>
  );
}

function ClipView({ it }: { it: Of<"clip"> }) {
  const { data: p } = useProject(it.projectId);
  const sh = p?.shots[it.sceneId];
  const url = it.url ?? sh?.clip?.url;
  const n = sceneNo(p, it.sceneId);
  const title = n ? `Scene ${n} clip` : "The clip";
  return (
    <div className="stg-item">
      <Shot src={sh?.frame?.url} aspect={aspectOf(p)} empty="The clip isn't made yet">
        {url && (
          <button data-action="stage.play" type="button" className="stg-play" onClick={() => playNow({ url, title, what: "clip", projectId: it.projectId, sceneId: it.sceneId })} aria-label={`Play ${title}`}>
            <Icon name="play" size={26} />
          </button>
        )}
      </Shot>
      <p className="stg-meta">
        {title}
        {sh?.clip ? ` · ${sh.clip.durationS} s` : ""}
      </p>
    </div>
  );
}

function VideoView({ it }: { it: Of<"video"> }) {
  const { data: p } = useProject(it.projectId);
  const { data: versions } = useStageLoad(`versions:${it.projectId}`, (src) => src.versions(it.projectId));
  const v = versions?.find((x) => (it.version ? x.version === it.version : it.renderId ? x.renderId === it.renderId : x.isLatest)) ?? null;
  const url = it.url ?? v?.url ?? null;
  const title = `${p?.title ?? "Your video"}${v ? `, v${v.version}` : ""}`;
  return (
    <div className="stg-item">
      <Shot src={v?.thumbUrl ?? p?.thumbUrl} aspect={aspectOf(p)} empty="Not rendered yet">
        {url && (
          <button data-action="stage.play" type="button" className="stg-play" onClick={() => playNow({ url, title, what: "render", projectId: it.projectId, version: v?.version })} aria-label={`Play ${title}`}>
            <Icon name="play" size={26} />
          </button>
        )}
      </Shot>
      <p className="stg-meta">{v ? `Version ${v.version} · ${Math.round(v.durationS)} s` : "Not rendered yet"}</p>
    </div>
  );
}

function ScriptView({ it }: { it: Of<"script"> }) {
  const { data: p, error } = useProject(it.projectId);
  if (error) return <Failed what="the script" />;
  if (!p) return <Loading what="the script" />;
  if (!p.script) return <p className="stg-wait">No script yet.</p>;
  return (
    <ol className="stg-script">
      {p.script.scenes.map((s, i) => (
        <li key={s.id}>
          <span className="stg-script-n">{i + 1}</span>
          <span>
            <span className="stg-script-vo">{s.voiceover || s.lines?.map((l) => `${l.speaker}: ${l.text}`).join(" ") || s.onScreenText}</span>
            {s.onScreenText && s.voiceover && <span className="stg-script-ost">On screen: {s.onScreenText}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

function EstimateView({ it }: { it: Of<"estimate"> }) {
  const { data: e, error } = useStageLoad(`estimate:${it.projectId}`, (src) => src.estimate(it.projectId));
  const balance = useStore((s) => s.balance);
  if (error) return <Failed what="the price" />;
  if (!e) return <Loading what="the price" />;
  return (
    <div className="stg-estimate">
      <dl>
        {e.breakdown.map((l, i) => (
          <div key={i}>
            <dt>
              {l.label}
              {l.detail && <span>{l.detail}</span>}
            </dt>
            <dd>{l.credits}</dd>
          </div>
        ))}
        <div className="stg-total">
          <dt>Total</dt>
          <dd>{e.credits} credits</dd>
        </div>
      </dl>
      {balance != null && <p className="stg-meta">You have {balance}.</p>}
    </div>
  );
}

// ------------------------------------------------------------------ channel things

function CollectionView({ it }: { it: Of<"collection"> }) {
  const { data: c, error } = useStageLoad(`collection:${it.collectionId}`, (src) => src.collection(it.collectionId));
  if (error) return <Failed what="the collection" />;
  if (!c) return <Loading what="the collection" />;
  return (
    <div className="stg-item">
      <Shot src={c.thumbUrl} empty={c.name} />
      <p className="stg-meta">
        {c.kind[0]!.toUpperCase() + c.kind.slice(1)} · {c.videos} {c.videos === 1 ? "video" : "videos"}
        {c.needsYou ? ` · ${c.needsYou} need you` : ""}
      </p>
      <button data-action="stage.open" type="button" className="btn btn-quiet btn-small" onClick={() => openScreen(`/k/${c.id}`)}>
        Open {c.name}
      </button>
    </div>
  );
}

const STATE_WORD: Record<string, string> = { approved: "approved", draft: "waiting for your OK", making: "being drawn", none: "no sheet yet" };

function CharacterView({ it }: { it: Of<"character"> }) {
  const { data: c, error } = useStageLoad(`character:${it.characterId}`, (src) => src.character(it.characterId));
  if (error) return <Failed what="the character" />;
  if (!c) return <Loading what="the character" />;
  const v = c.versions.find((x) => x.n === c.approved) ?? c.versions[0];
  return (
    <div className="stg-item">
      <Shot src={v?.sheets.turnaround ?? v?.panels.front} empty="No sheet yet" />
      <p className="stg-meta">
        {v ? `v${v.n} · ` : ""}
        {STATE_WORD[c.state] ?? c.state}
        {c.voiceName ? ` · ${c.voiceName}` : ""}
      </p>
      {c.look && <p className="stg-quote">{c.look}</p>}
    </div>
  );
}

function SheetPanels({ v, group }: { v: StageSheetVersion | undefined; group: SheetGroup }) {
  const g = SHEET_GROUPS.find((x) => x.id === group)!;
  return (
    <ul className={`stg-panels stg-panels-${group}`}>
      {g.panels.map((p) => (
        <li key={p.id}>
          <span className="stg-panel">{v?.panels[p.id] ? <img src={v.panels[p.id]!} alt={p.label} /> : <span className="stg-shot-empty">…</span>}</span>
          <span className="stg-panel-label">{p.label}</span>
        </li>
      ))}
    </ul>
  );
}

const verWord = (c: StageCharacter, v: StageSheetVersion) => `v${v.n} · ${v.approved || c.approved === v.n ? "approved" : v.status === "making" ? "being drawn" : "draft"}`;

function SheetView({ it }: { it: Of<"sheet"> }) {
  const { data: c, error } = useStageLoad(`character:${it.characterId}`, (src) => src.character(it.characterId));
  const [group, setGroup] = useState<SheetGroup>(it.group ?? "angles");
  if (error) return <Failed what="the sheet" />;
  if (!c) return <Loading what="the sheet" />;
  const wanted = it.versions.length ? it.versions : [c.versions[0]?.n ?? 0];
  const shown = wanted.map((n) => c.versions.find((v) => v.n === n)).filter((v): v is StageSheetVersion => !!v);
  return (
    <div className="stg-sheet">
      <div className="stg-tabs" role="tablist" aria-label="Sheet panels">
        {SHEET_GROUPS.map((g) => (
          <button key={g.id} type="button" role="tab" aria-selected={group === g.id} className={`stg-tab${group === g.id ? " is-on" : ""}`} onClick={() => setGroup(g.id)}>
            {g.label}
          </button>
        ))}
      </div>
      <div className={`stg-compare${shown.length > 1 ? " is-two" : ""}`}>
        {(shown.length ? shown : [undefined]).map((v, i) => (
          <section key={v?.n ?? i}>
            {v && <p className="stg-ver">{verWord(c, v)}</p>}
            <SheetPanels v={v} group={group} />
          </section>
        ))}
      </div>
    </div>
  );
}

function LibraryView({ it }: { it: Of<"library"> }) {
  const { data: items, error } = useStageLoad(`library:${it.scope}`, (src) => src.library(it.scope));
  if (error) return <Failed what="the library" />;
  if (!items) return <Loading what="the library" />;
  const pics = items.filter((a) => a.thumbUrl || (a.kind === "image" && a.url) || a.kind === "logo");
  return (
    <div className="stg-item">
      <ul className="stg-grid">
        {pics.slice(0, 9).map((a) => (
          <li key={a.id}>
            <span className="stg-panel">{a.thumbUrl || a.url ? <img src={(a.thumbUrl ?? a.url)!} alt={a.name} /> : <Icon name="image" />}</span>
          </li>
        ))}
      </ul>
      <p className="stg-meta">{items.length} {items.length === 1 ? "item" : "items"}</p>
      <button data-action="stage.open" type="button" className="btn btn-quiet btn-small" onClick={() => openScreen(`/library${it.scope === "account" ? "" : `?scope=${encodeURIComponent(it.scope)}`}`)}>
        Open the library
      </button>
    </div>
  );
}

function ModelsView({ it }: { it: Of<"models"> }) {
  const { data, error } = useStageLoad(`models:${it.step ?? "all"}`, (src) => src.models(it.step));
  if (error) return <Failed what="the models" />;
  if (!data) return <Loading what="the models" />;
  const list = it.step && it.step !== "all" ? data.filter((m) => m.step === it.step) : data;
  return (
    <ul className="stg-list">
      {list.slice(0, 8).map((m) => (
        <li key={m.id} className={m.isDefault ? "is-on" : ""}>
          <span className="stg-list-main">
            <strong>{m.label}</strong>
            <span>{m.strengths.slice(0, 2).join(", ")}</span>
          </span>
          <span className="stg-list-side">{m.creditsPer5s != null ? `${m.creditsPer5s} / 5 s` : m.credits != null ? `${m.credits}` : ""}</span>
        </li>
      ))}
    </ul>
  );
}

function FormatsView({ it }: { it: Of<"formats"> }) {
  const { data, error } = useStageLoad("formats", (src) => src.formats());
  if (error) return <Failed what="the video types" />;
  if (!data) return <Loading what="the video types" />;
  const hi = new Set(it.highlight ?? []);
  const list = [...data].sort((a, b) => Number(hi.has(b.id)) - Number(hi.has(a.id)));
  return (
    <ul className="stg-list">
      {list.slice(0, 8).map((f) => (
        <li key={f.id} className={hi.has(f.id) ? "is-on" : ""}>
          <span className="stg-list-main">
            <strong>{f.name}</strong>
            <span>{f.tagline}</span>
          </span>
          {hi.has(f.id) && <span className="stg-list-side">Suggested</span>}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ v4: references (V4-D4) and the Suno pack (V4-D3)

/** A scene's reference pictures for its next frame, in the order they're sent, each with why it was picked. */
function ReferencesStageView({ it }: { it: Of<"references"> }) {
  const { data: p } = useProject(it.projectId);
  const { data: v, error } = useStageLoad(`refs:${it.projectId}/${it.sceneId}`, () => api.references(it.projectId, it.sceneId));
  const n = sceneNo(p, it.sceneId);
  if (error) return <Failed what="the reference pictures" />;
  if (!v) return <Loading what="the reference pictures" />;
  return (
    <div className="stg-item stg-refs">
      {warningNotices(v).map((w, i) => (
        <Notice key={`${w.code}-${i}`} tone={w.tone} code={w.code} text={w.text} />
      ))}
      {v.budget.max <= 0 ? (
        <p className="stg-wait">{v.budget.modelShort} draws from the words alone.</p>
      ) : v.picked.length ? (
        <ol className="stg-list stg-refs-list">
          {v.picked.map((r, i) => (
            <li key={`${r.assetId}-${i}`}>
              <span className="stg-ref-pic">{r.url ? <img src={r.url} alt="" /> : <Icon name="image" />}</span>
              <span className="stg-list-main">
                <strong>
                  {i + 1}. {r.label}
                </strong>
                {r.reason && <span>{r.reason}</span>}
              </span>
              {r.pinned && <span className="stg-list-side">Pinned</span>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="stg-wait">No reference pictures for this scene.</p>
      )}
      <p className="stg-meta">
        {n ? `Scene ${n} · ` : ""}
        {budgetLine(v)}
      </p>
      {n > 0 && (
        <button
          type="button"
          className="btn btn-quiet btn-small"
          onClick={() => {
            writeFold("scene.references", true);
            openScreen(`/p/${it.projectId}?scene=${n}`);
          }}
        >
          Open the scene
        </button>
      )}
    </div>
  );
}

/** The video's Suno pack: the fields to copy, the settings and the song upload (components/SunoPack.tsx). */
function SunoStageView({ it }: { it: Of<"suno"> }) {
  const { data: p, error } = useProject(it.projectId);
  if (error) return <Failed what="the Suno pack" />;
  if (!p) return <Loading what="the Suno pack" />;
  return (
    <div className="stg-item stg-suno">
      {p.suno?.notices.map((x, i) => <Notice key={`${x.code ?? x.tone}-${i}`} {...x} />)}
      <SunoPackBody p={p} />
    </div>
  );
}

/** v4 (V4-D1): the same showcase shot on 2-4 video models, side by side (model_advice / show_showcase). */
function ShowcaseView({ it }: { it: Of<"showcase"> }) {
  return it.step === "frame" ? <StillsView it={it} /> : <ClipsView it={it} />;
}

/** MS-IMG: the same prompt's stills on 2-4 picture models (model_advice / show_showcase with step frame). */
function StillsView({ it }: { it: Of<"showcase"> }) {
  const { data, error } = useStageLoad("model-showcase-images", async () => {
    const d = await loadImageShowcase();
    if (!d) throw new Error("no image showcase");
    return d;
  });
  if (error) return <Failed what="the picture showcase" />;
  if (!data) return <Loading what="the picture showcase" />;
  if (compareStills(data, it.style, it.models).length < 2) return <Failed what="those showcase pictures" />;
  return <StillCompare data={data} rowId={it.style} models={it.models} />;
}

function ClipsView({ it }: { it: Of<"showcase"> }) {
  const { data, error } = useStageLoad("model-showcase", async () => {
    const d = await loadModelShowcase();
    if (!d) throw new Error("no showcase");
    return d;
  });
  if (error) return <Failed what="the model showcase" />;
  if (!data) return <Loading what="the model showcase" />;
  const clips = compareClips(data, it.style, it.models);
  if (clips.length < 2) return <Failed what="those showcase clips" />;
  return <ShowcaseCompare clips={clips} />;
}

/** The view for one item. Choices and auditions are drawn by StageView itself. */
export function ItemView({ it }: { it: StageItem }) {
  switch (it.kind) {
    case "project":
      return <ProjectView it={it} />;
    case "frame":
      return <FrameView it={it} />;
    case "clip":
      return <ClipView it={it} />;
    case "picture":
      return <PictureView it={it} />;
    case "video":
      return <VideoView it={it} />;
    case "script":
      return <ScriptView it={it} />;
    case "estimate":
      return <EstimateView it={it} />;
    case "collection":
      return <CollectionView it={it} />;
    case "character":
      return <CharacterView it={it} />;
    case "sheet":
      return <SheetView it={it} />;
    case "library":
      return <LibraryView it={it} />;
    case "models":
      return <ModelsView it={it} />;
    case "formats":
      return <FormatsView it={it} />;
    case "references":
      return <ReferencesStageView it={it} />;
    case "suno":
      return <SunoStageView it={it} />;
    case "showcase":
      return <ShowcaseView it={it} />;
    default:
      return null;
  }
}
